import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency, formatDate, getStatusBadgeStyle } from '../helpers';

const ModalDetalleGastos = ({ show, onClose, gasto }) => {
    if (!show || !gasto) return null;

    const esImpuesto = gasto.tipoComprobante === 'Impuestos / Tasas Fiscales';
    const esPrestamo = gasto.tipoComprobante === 'Préstamo';
    const esModoDirecto = esImpuesto || esPrestamo;

    const monedaActual = gasto.moneda || 'ARS';
    const simboloMoneda = monedaActual === 'USD' ? 'U$S' : '$';
    const monedaSimbolo = monedaActual === 'USD' ? 'USD' : 'ARS';

    const percepcionesList = Array.isArray(gasto.percepcionesList) ? gasto.percepcionesList : [];
    const pagosList = Array.isArray(gasto.pagosList) ? gasto.pagosList : [];
    const chequesList = Array.isArray(gasto.chequesList) ? gasto.chequesList : [];
    const cuotasPlan = Array.isArray(gasto.cuotasPlan) ? gasto.cuotasPlan : [];

    const tienePlanPago = gasto.esPlanPago && cuotasPlan.length > 0;
    const tieneCheques = chequesList.length > 0;

    const egresoDebitadoReal = gasto.montoEgresoBanco !== undefined && gasto.montoEgresoBanco !== '' 
        ? parseFloat(gasto.montoEgresoBanco) || 0 
        : 0;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-auto max-h-[95vh] flex flex-col overflow-hidden border border-gray-100 transition-all duration-300">
                
                {/* HEADER UNIFICADO */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-blue-50/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-600 p-2.5 rounded-xl shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </span>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 leading-none">
                                Detalle de Egreso / Gasto
                            </h3>
                            <p className="text-[10px] text-blue-800/80 font-black mt-1.5 tracking-wider uppercase select-none">
                                {gasto.tipoComprobante || 'Comprobante'} N° {gasto.numeroComprobante || 'S/N'} • {gasto.proveedorNombre || 'Proveedor/Fisco'} ({monedaActual})
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-slate-50 rounded-full cursor-pointer"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* CUERPO DEL MODAL CON SCROLL */}
                <div className="overflow-y-auto p-8 space-y-6 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    
                    {/* BANNER EJECUTIVO RESUMEN ESTADO Y CONCILIACIÓN */}
                    <div className="bg-slate-50/80 border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                            
                            {/* ESTATUS DE PAGO */}
                            <div className="space-y-1">
                                <span className="block text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                                    Estatus de Pago
                                </span>
                                <span className={`inline-block px-3.5 py-1 rounded-full text-xs font-black uppercase border ${getStatusBadgeStyle(gasto.estatus)}`}>
                                    ● {gasto.estatus || 'Pendiente de Pago'}
                                </span>
                            </div>

                            {/* SEMÁFORO DE CONCILIACIÓN CBU */}
                            <div className="space-y-1 sm:border-l sm:border-slate-200/80 sm:pl-4">
                                <span className="block text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                                    Conciliación CBU
                                </span>
                                {gasto.montoEgresoBanco === undefined || gasto.montoEgresoBanco === '' ? (
                                    <span className="text-xs font-bold text-slate-400 italic">
                                        Sin Registro en Banco
                                    </span>
                                ) : (
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-xl text-xs font-mono font-black border ${
                                        gasto.diferenciaEgreso === 0
                                            ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                            : gasto.diferenciaEgreso < 0
                                                ? 'bg-amber-50 text-amber-800 border-amber-300'
                                                : 'bg-rose-50 text-rose-800 border-rose-300'
                                    }`}>
                                        {gasto.diferenciaEgreso === 0 ? (
                                            '✓ Conciliado 100%'
                                        ) : (
                                            `${gasto.diferenciaEgreso < 0 ? 'Excedente: ' : 'Pendiente: '} ${formatCurrency(Math.abs(gasto.diferenciaEgreso || 0), monedaSimbolo)}`
                                        )}
                                    </span>
                                )}
                            </div>

                        </div>
                    </div>

                    {/* SECCIÓN I: COMPROBANTE Y PROVEEDOR / FISCO */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                        <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                            Comprobante e Identificación
                        </legend>
                        
                        <div className="space-y-4 pt-2">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Fecha Comprobante</span>
                                    <span className="font-mono font-bold text-slate-900 text-sm">{formatDate(gasto.fechaGasto || gasto.fechaIngreso)}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Fecha Vencimiento</span>
                                    <span className="font-mono font-bold text-slate-900 text-sm">{formatDate(gasto.fechaVencimientoGasto || gasto.fechaGasto)}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Tipo Comprobante</span>
                                    <span className="font-bold text-slate-900 text-sm">{gasto.tipoComprobante || '---'}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">N° Comprobante</span>
                                    <span className="font-mono font-bold text-indigo-700 text-sm">{gasto.numeroComprobante || 'S/N'}</span>
                                </div>
                            </div>

                            <div className="bg-slate-50/70 p-3.5 rounded-xl border border-slate-200/80">
                                <span className="block text-[14px] font-bold text-slate-800 mb-1">Proveedor / Fisco / Entidad</span>
                                <span className="font-black text-slate-900 uppercase text-sm">{gasto.proveedorNombre || '---'}</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Área Presupuestaria</span>
                                    <span className="font-bold text-slate-800 uppercase text-xs">{gasto.areaNombre || 'Sin área asignada'}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Concepto del Gasto</span>
                                    <span className="font-bold text-slate-800 uppercase text-xs">{gasto.conceptoNombre || 'Sin concepto'}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Subconcepto</span>
                                    <span className="font-bold text-slate-800 uppercase text-xs">{gasto.subconceptoNombre || 'Sin subconcepto'}</span>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {/* SECCIÓN II: CÁLCULO DE IMPORTES, TRIBUTOS E IMPUESTOS */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                        <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                            {esImpuesto 
                                ? 'Cálculo del Impuesto / Tasa' 
                                : esPrestamo 
                                    ? 'Cálculo del Préstamo' 
                                    : 'Cálculo del Gasto e Impuestos'}
                        </legend>

                        <div className="space-y-4 pt-2">
                            {esModoDirecto ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-indigo-50/60 p-4 rounded-xl border border-indigo-100">
                                        <span className="block text-[10px] font-black text-indigo-900 uppercase tracking-wider mb-1">
                                            {esPrestamo ? `Monto Neto Préstamo (${simboloMoneda})` : `Monto Impuesto / Tasa Directa (${simboloMoneda})`}
                                        </span>
                                        <span className="font-mono text-xl font-black text-indigo-950">{formatCurrency(gasto.montoNeto || gasto.montoTotal, monedaSimbolo)}</span>
                                    </div>
                                    <div className="bg-indigo-900 text-white p-4 rounded-xl flex flex-col justify-center shadow-xs">
                                        <span className="text-[10px] font-black uppercase tracking-wider opacity-80">Monto Total a Liquidar</span>
                                        <span className="font-mono text-xl font-black mt-0.5">{formatCurrency(gasto.montoTotal, monedaSimbolo)}</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                            <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Monto Neto Gravado ({simboloMoneda})</span>
                                            <span className="font-mono text-lg font-bold text-slate-900">{formatCurrency(gasto.montoNeto, monedaSimbolo)}</span>
                                        </div>

                                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                            <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Tasa de IVA Aplicada</span>
                                            <span className="font-mono text-lg font-bold text-slate-900">{gasto.tasaIva !== undefined ? `${gasto.tasaIva}%` : '21%'}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
                                        <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-300 flex flex-col justify-center">
                                            <span className="text-[10px] font-black text-blue-800 uppercase tracking-wider">Monto IVA</span>
                                            <span className="font-mono text-base font-bold text-blue-800 mt-0.5">{formatCurrency(gasto.montoIva, monedaSimbolo)}</span>
                                        </div>

                                        <div className="bg-indigo-50/70 p-4 rounded-xl border border-indigo-300 flex flex-col justify-center">
                                            <span className="text-[10px] font-black text-indigo-800 uppercase tracking-wider">Total Percepciones</span>
                                            <span className="font-mono text-base font-bold text-indigo-800 mt-0.5">{formatCurrency(gasto.totalPercepciones, monedaSimbolo)}</span>
                                        </div>

                                        <div className="bg-purple-50/70 p-4 rounded-xl border border-purple-300 flex flex-col justify-center">
                                            <span className="text-[10px] font-black text-purple-800 uppercase tracking-wider">Otros Tributos</span>
                                            <span className="font-mono text-base font-bold text-purple-800 mt-0.5">{formatCurrency(gasto.montoOtrosTributos, monedaSimbolo)}</span>
                                        </div>

                                        <div className="bg-rose-50/70 text-rose-800 p-4 rounded-xl border border-rose-200 flex flex-col justify-center shadow-2xs">
                                            <span className="text-[10px] font-black uppercase tracking-wider opacity-90">Monto Total Factura</span>
                                            <span className="font-mono text-lg font-black text-rose-800 mt-0.5">{formatCurrency(gasto.montoTotal, monedaSimbolo)}</span>
                                        </div>
                                    </div>

                                    {/* DESGLOSE DE PERCEPCIONES SI EXISTEN */}
                                    {percepcionesList.length > 0 && (
                                        <div className="p-4 bg-indigo-50/35 rounded-xl border border-indigo-200 space-y-2">
                                            <span className="text-xs font-black text-indigo-900 uppercase tracking-wider block border-b border-indigo-100 pb-1">
                                                🏛️ Desglose de Percepciones Sufridas ({percepcionesList.length})
                                            </span>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                {percepcionesList.map((perc, idx) => (
                                                    <span key={idx} className="inline-flex items-center gap-2 bg-white border border-indigo-200 text-slate-800 font-bold px-3 py-1 rounded-xl text-xs shadow-2xs">
                                                        <span className="text-indigo-900 uppercase">{perc.tipo}:</span>
                                                        <span className="font-mono text-sm">{formatCurrency(perc.monto, monedaSimbolo)}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </fieldset>

                    {/* SECCIÓN III: TESORERÍA, DÉBITOS BANCO, CHEQUES Y PLANES DE PAGO */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                        <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                            Tesorería, Débitos y Medios de Pago
                        </legend>

                        <div className="space-y-6 pt-2">
                            
                            {/* PAGOS ASIGNADOS MÚLTIPLES */}
                            {pagosList.length > 0 && (
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                                    <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest block border-b border-slate-200 pb-1">
                                        Desglose de Formas de Pago Asignadas ({pagosList.length})
                                    </span>
                                    <div className="space-y-2 pt-1">
                                        {pagosList.map((p, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-200 text-xs font-semibold shadow-2xs">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-indigo-900 uppercase">{p.formaPago}</span>
                                                    {p.cuentaNombre && <span className="text-slate-500 uppercase">({p.cuentaNombre})</span>}
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono text-base font-black text-slate-900">{formatCurrency(p.monto, monedaSimbolo)}</span>
                                                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                                                        p.pagado ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                    }`}>
                                                        {p.pagado ? '✓ Pagado' : '⏳ Pendiente'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* PLAN DE PAGOS CUOTA POR CUOTA */}
                            {tienePlanPago && (
                                <div className="p-4 bg-indigo-50/40 rounded-xl border border-indigo-200 space-y-3">
                                    <div className="flex justify-between items-center border-b border-indigo-200 pb-1.5">
                                        <span className="text-xs font-black text-indigo-950 uppercase tracking-wider flex items-center gap-1.5">
                                            🏛️ Plan de Pago en Cuotas ({cuotasPlan.length} cuotas)
                                        </span>
                                        <span className="font-mono text-xs font-bold text-indigo-900 bg-white px-2.5 py-0.5 rounded-lg border border-indigo-200">
                                            Debitado: {formatCurrency(egresoDebitadoReal, monedaSimbolo)} / {formatCurrency(gasto.montoTotal, monedaSimbolo)}
                                        </span>
                                    </div>

                                    <div className="space-y-2 pt-1">
                                        {cuotasPlan.map((c, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-xl border border-indigo-100 text-xs font-semibold shadow-2xs">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-extrabold text-indigo-950 uppercase">Cuota N° {c.numero}</span>
                                                    <span className="text-slate-500">Vence: <strong className="text-slate-800">{c.fechaVencimiento}</strong></span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono text-base font-black text-indigo-950">{formatCurrency(c.monto, monedaSimbolo)}</span>
                                                    <span className={`px-3 py-0.5 rounded-full text-[10px] font-black uppercase ${
                                                        c.pagada ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                    }`}>
                                                        {c.pagada ? '✓ Pagado' : '⏳ Pendiente'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* MÓDULO CHEQUES ASOCIADOS */}
                            {tieneCheques && (
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                                    <div className="flex justify-between items-center border-b border-slate-200 pb-1.5">
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-wider">
                                            📜 Cheques Emitidos ({chequesList.length})
                                        </span>
                                        <span className="font-mono text-xs font-bold text-slate-800 bg-white px-2.5 py-0.5 rounded-lg border border-slate-200">
                                            Debitado: {formatCurrency(egresoDebitadoReal, monedaSimbolo)}
                                        </span>
                                    </div>

                                    <div className="space-y-2 pt-1">
                                        {chequesList.map((chk, idx) => {
                                            const estaPagado = !!chk.pagada;
                                            return (
                                                <div key={idx} className="bg-white p-3 rounded-xl border border-slate-200 text-xs font-semibold shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-3">
                                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                                        <span className="font-mono font-extrabold text-slate-900 bg-slate-100 px-2 py-0.5 rounded shrink-0">
                                                            N° {chk.numero}
                                                        </span>
                                                        <span className="text-slate-600 uppercase font-bold truncate" title={chk.cuentaNombre || chk.banco || 'S/D'}>
                                                            {chk.cuentaNombre || chk.banco || 'S/D'}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center justify-between md:justify-end gap-3 shrink-0 border-t md:border-t-0 pt-2 md:pt-0 border-slate-100">
                                                        <span className="text-slate-400 text-[10px] font-bold uppercase shrink-0">
                                                            Vence: <strong className="text-slate-700 font-mono text-xs">{chk.fechaVencimiento}</strong>
                                                        </span>

                                                        <span className="font-mono text-base font-black text-slate-900 shrink-0">
                                                            {formatCurrency(chk.monto, monedaSimbolo)}
                                                        </span>

                                                        <span className={`px-3 py-0.5 rounded-full text-[10px] font-black uppercase shrink-0 ${
                                                            estaPagado ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                        }`}>
                                                            {estaPagado ? '✓ Pagado' : '⏳ Pendiente'}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* FORMA DE PAGO DE IMPUESTO Y CUENTA BANCARIA DE ORIGEN */}
                            {esImpuesto && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-indigo-50/50 p-3.5 rounded-xl border border-indigo-200">
                                        <span className="block text-[11px] font-bold text-indigo-900 uppercase mb-1">Forma de Pago Impuesto</span>
                                        <span className="font-bold text-slate-900 uppercase text-xs">{gasto.formaPagoImpuesto || 'No especificada'}</span>
                                    </div>
                                    <div className="bg-indigo-50/50 p-3.5 rounded-xl border border-indigo-200">
                                        <span className="block text-[11px] font-bold text-indigo-900 uppercase mb-1">Cuenta Bancaria CBU Origen</span>
                                        <span className="font-bold text-slate-900 uppercase text-xs">{gasto.cuentaNombre || gasto.cuentaNombreOrigen || 'Sin asignar'}</span>
                                    </div>
                                </div>
                            )}

                            {/* AUDITORÍA DE TESORERÍA DE EGRESO */}
                            <div className="p-5 rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/50 via-white to-slate-50 shadow-sm space-y-4">
                                <div className="flex items-center justify-between border-b border-indigo-100 pb-2.5">
                                    <div className="flex items-center gap-2">
                                        <span className="p-1.5 bg-indigo-100 text-indigo-700 rounded-lg">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                        </span>
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-wider">Auditoría de Tesorería ({monedaActual})</span>
                                    </div>
                                    <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full uppercase tracking-widest">
                                        Estado Financiero
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-2xs">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Devengado</span>
                                        <span className="font-mono text-lg font-black text-indigo-950 mt-1">
                                            {formatCurrency(gasto.montoTotal, monedaSimbolo)}
                                        </span>
                                        <span className="text-[10px] font-semibold text-slate-400 mt-1">
                                            Importe total a liquidar
                                        </span>
                                    </div>

                                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-2xs">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Debitado</span>
                                        <span className="font-mono text-lg font-black text-slate-900 mt-1">
                                            {egresoDebitadoReal > 0 ? formatCurrency(egresoDebitadoReal, monedaSimbolo) : '---'}
                                        </span>
                                        <span className="text-[10px] font-semibold text-slate-400 mt-1">
                                            Sumatoria cobrada / debitada
                                        </span>
                                    </div>

                                    <div className="flex flex-col justify-center">
                                        <div className={`h-full px-4 py-3 rounded-xl border text-center font-mono font-black text-xs shadow-2xs flex flex-col items-center justify-center ${
                                            gasto.diferenciaEgreso === 0
                                                ? 'bg-emerald-50/70 text-emerald-800 border-emerald-500'
                                                : gasto.diferenciaEgreso < 0
                                                    ? 'bg-amber-500 text-white border-amber-600'
                                                    : 'bg-rose-500 text-white border-rose-600'
                                        }`}>
                                            {gasto.diferenciaEgreso === 0 ? (
                                                <span className="text-sm uppercase tracking-wider flex items-center gap-1">
                                                    ✓ Conciliado 100%
                                                </span>
                                            ) : (
                                                <>
                                                    <span className="text-[10px] uppercase font-bold opacity-90 tracking-widest">
                                                        {gasto.diferenciaEgreso < 0 ? 'Excedente en Débito' : 'Pendiente de Conciliar'}
                                                    </span>
                                                    <span className="text-lg font-mono font-black mt-0.5">
                                                        {formatCurrency(Math.abs(gasto.diferenciaEgreso || 0), monedaSimbolo)}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </fieldset>

                </div>

                {/* BOTONERA INFERIOR */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end bg-gray-50 shrink-0">
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="px-8 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-sm transition-all transform active:scale-95 select-none cursor-pointer"
                    >
                        Cerrar
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default memo(ModalDetalleGastos);